import * as yup from 'yup';
import { IInvestmentDataModel, IMySecurityTransaction } from '../data-models/investment.data-model';
import { isoDateStringField } from './isoDateStringField';

/**
 * A deliberately basic schema of the round-tracker query row
 * (`/metrics/roundtracker/query` → IInvestmentDataModel): first-level scalar
 * fields plus `myTransactions` as an array of records. customData (dynamic
 * keys) and logoUrl are intentionally omitted. First consumer is the
 * formula-field vocabulary (field existence + type; an array of records is a
 * list a formula can REDUCE over); the `satisfies` clauses keep every key
 * honest against the data model.
 */
export function myTransactionFields() {
  return {
    noOfShares: yup.number(),
    pps: yup.number(),
    security: yup.string(),
    transactionType: yup.string(),
  } satisfies Record<keyof IMySecurityTransaction, yup.ISchema<unknown>>;
}

export function roundTrackerFields() {
  return {
    coInvestors: yup.array().of(yup.string()),
    company: yup.string(),
    companyId: yup.number(),
    currentSharesHeld: yup.number(),
    discountRate: yup.number(),
    distributions: yup.number(),
    fmv: yup.number(),
    fullyDilutedShares: yup.number(),
    fund: yup.string(),
    gcAmount: yup.number(),
    interestRate: yup.number(),
    investmentDate: isoDateStringField(),
    investmentRoundId: yup.number(),
    leadInvestor: yup.string(),
    leadInvestors: yup.array().of(yup.string()),
    moic: yup.number(),
    myTransactions: yup.array().of(yup.object(myTransactionFields())),
    noOfShares: yup.number(),
    ownershipPercentage: yup.number(),
    position: yup.string(),
    postMoneyValuation: yup.number(),
    preMoneyValuation: yup.number(),
    // The data model allows a legacy string here, but the schema contract is
    // number | null; a string runtime value errors the arithmetic into a
    // blank cell rather than being coerced.
    pricePerShare: yup.number(),
    raiseAmount: yup.number(),
    restructure: yup.string(),
    round: yup.string(),
    roundId: yup.number(),
    secondary: yup.boolean(),
    security: yup.string(),
    sequence: yup.string(),
    transactionType: yup.string(),
    type: yup.string(),
    valuationCap: yup.number(),
    xirr: yup.number().label('XIRR'),
  } satisfies Partial<Record<keyof IInvestmentDataModel, yup.ISchema<unknown>>>;
}

export function roundTrackerSchema() {
  return yup.object(roundTrackerFields());
}
